import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Search } from 'lucide-react';
import {
  DescriptionText,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  HorizontalStack,
  PaneFooter,
  PaneHeader,
  PaneSection,
  PropertyGrid,
  PropertyItem,
  RegistryStatus,
  SectionSeparator,
  Surface,
  VerticalStack,
  CommerceCatalogWorkspace,
  Checkbox,
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';
import { useScope } from '@workspace/scope-context';
import { useNavigation } from '@/context/NavigationContext';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  CommerceBreadcrumbHeader,
  CommerceRegistryUtilityBand,
} from '@/views/commerce/catalogHierarchyShared';
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const COMMISSION_LEDGER_SORT_FIELDS = ['friendly_order_id', 'status', 'amount', 'selling_tenant_id', 'commission_plan_id', 'created_at', 'updated_at'] as const;
import {
  getCommissionLedgerEntry,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  getResellerCommissionSummary,
  listCommissionLedger,
  type CommissionLedgerWorkspace,
  type CommissionLedgerEntry,
  type CommissionLedgerLine,
  type CommerceFacetBucket,
  type ResellerCommissionSummary,
} from '@/lib/commerce';
import CommissionLedgerEntryView from './CommissionLedgerEntryView';

function formatDate(value?: string | null): string {
  if (!value) {
    return 'Not recorded';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function formatJSON(value: unknown): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function commissionOrderReference(entry: Pick<CommissionLedgerEntry, 'friendly_order_id' | 'order_id'>): string {
  return entry.friendly_order_id || entry.order_id;
}

function buildLedgerSectionRoute(sectionId: string, workspace: CommissionLedgerWorkspace): string {
  if (workspace === 'reseller') {
    if (sectionId === 'commission_ledger') {
      return '/reseller/commission-ledger';
    }
    if (sectionId === 'carts') {
      return '/reseller/carts';
    }
    if (sectionId === 'orders') {
      return '/reseller/orders';
    }
  }
  return buildCommerceSectionRoute(sectionId);
}

function commissionStatusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'paid':
      return 'success';
    case 'approved':
      return 'default';
    case 'reversed':
      return 'error';
    case 'earned':
    case 'pending':
      return 'warning';
    case 'void':
      return 'muted';
    default:
      return 'muted';
  }
}

interface CommissionLedgerPageProps {
  workspace?: CommissionLedgerWorkspace;
}

function sumLedgerAmount(entries: CommissionLedgerEntry[]): string {
  const total = entries.reduce((sum, entry) => {
    const parsed = Number.parseFloat(entry.amount || '0');
    return Number.isFinite(parsed) ? sum + parsed : sum;
  }, 0);
  return total.toFixed(2);
}

function sumStatusAmounts(summary: ResellerCommissionSummary | null, status?: string): string {
  if (!summary) return '0.00';
  let totals: { amount: string; currency: string }[] = [];
  if (status) {
    totals = summary.commission_totals_by_status[status] || [];
  } else {
    totals = Object.values(summary.commission_totals_by_status).flat();
  }
  const sum = totals.reduce((acc, curr) => {
    const val = Number.parseFloat(curr.amount);
    return Number.isFinite(val) ? acc + val : acc;
  }, 0);
  return sum.toFixed(2);
}

function lineSummary(line: CommissionLedgerLine): string {
  return `${line.line_type} • ${line.amount} ${line.currency}`;
}

export default function CommissionLedgerPage({ workspace = 'catalog-owner' }: CommissionLedgerPageProps) {
  const { activeTenant } = useScope();
  const navigation = useNavigation();
  const [entries, setEntries] = useState<CommissionLedgerEntry[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<CommissionLedgerEntry | null>(null);
  const [summary, setSummary] = useState<ResellerCommissionSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryNotice, setSummaryNotice] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const query = useCommerceRegistryQuery({
    facetKeys: ['status'],
    facetValues: { status: ['pending', 'earned', 'approved', 'paid', 'reversed', 'void'] },
    sortFields: COMMISSION_LEDGER_SORT_FIELDS,
  });
  const [statusOptions, setStatusOptions] = useState<CommerceFacetBucket[]>([]);
  const requestSequence = useRef(0);
  const columnFilters = useMemo<ColumnFiltersState>(() =>
    query.facets.status.length > 0 ? [{ id: 'status', value: query.facets.status }] : [],
  [query.facets.status]);
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);

  const loadLedger = async () => {
    const requestID = ++requestSequence.current;
    setLoading(true);
    setError(null);
    setSummaryNotice(null);
    setAccessDeniedStatus(null);
    try {
      const paginationQuery = toLimitOffset(query.pagination);
      const [ledgerResult, summaryResult] = await Promise.allSettled([
        listCommissionLedger(workspace, {
          ...paginationQuery,
          ...(query.search ? { search: query.search } : {}),
          ...(query.facets.status.length > 0 ? { status: query.facets.status } : {}),
          ...(query.sort.length > 0 ? { sort: query.sort } : {}),
          include_facets: ['status'],
        }),
        workspace === 'reseller' ? getResellerCommissionSummary() : Promise.resolve(null),
      ]);

      if (ledgerResult.status === 'rejected') {
        throw ledgerResult.reason;
      }
      if (requestID !== requestSequence.current) return;

      const ledgerPage = ledgerResult.value;
      const nextPageIndex = lastPageIndexForTotal(ledgerPage.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setEntries(ledgerPage.items);
      setTotalEntries(ledgerPage.total_count);
      setStatusOptions(ledgerPage.facets?.status ?? []);
      setSelectedEntryId(current =>
        current && ledgerPage.items.some(entry => entry.commission_entry_id === current)
          ? current
          : null
      );

      if (workspace === 'reseller' && summaryResult.status === 'fulfilled') {
        setSummary(summaryResult.value);
      } else if (workspace === 'reseller') {
        setSummary(null);
        setSummaryNotice(
          getCommerceErrorMessage(summaryResult.reason, 'Reseller summary is currently unavailable for the active tenant.')
        );
      } else {
        setSummary(null);
      }
    } catch (err) {
      if (requestID !== requestSequence.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load commission ledger.'));
      }
      setEntries([]);
      setTotalEntries(0);
      setSelectedEntryId(null);
      setSelectedEntry(null);
      setSummary(null);
    } finally {
      if (requestID === requestSequence.current) setLoading(false);
    }
  };

  const loadDetail = async (entryId: string) => {
    setDetailLoading(true);
    try {
      const detail = await getCommissionLedgerEntry(workspace, entryId);
      setSelectedEntry(detail);
    } catch (err) {
      setError(getCommerceErrorMessage(err, 'Failed to load commission ledger detail.'));
      setSelectedEntry(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (!activeTenant?.id) {
      setEntries([]);
      setSelectedEntryId(null);
      setSelectedEntry(null);
      setSummary(null);
      setTotalEntries(0);
      setLoading(false);
      setDetailLoading(false);
      setError(null);
      setSummaryNotice(null);
      setAccessDeniedStatus(null);
      return;
    }
    void loadLedger();
  }, [activeTenant?.id, workspace, query.pagination.pageIndex, query.pagination.pageSize, query.search, query.sort.join('\u0000'), query.facets.status.join('\u0000')]);

  useEffect(() => {
    if (!activeTenant?.id || !selectedEntryId) {
      setSelectedEntry(null);
      return;
    }
    void loadDetail(selectedEntryId);
  }, [activeTenant?.id, selectedEntryId, workspace]);

  const selectedStatuses = useMemo(() => {
    return query.facets.status;
  }, [query.facets.status]);

  const rowSelection = useMemo<RowSelectionState>(() => {
    const rowIndex = entries.findIndex(entry => entry.commission_entry_id === selectedEntryId);
    return rowIndex >= 0 ? { [rowIndex]: true } : {};
  }, [entries, selectedEntryId]);

  const handleSortingChange: OnChangeFn<SortingState> = updater => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
  };

  const columns = useMemo<ColumnDef<CommissionLedgerEntry>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'friendly_order_id',
      accessorFn: entry => commissionOrderReference(entry),
      header: 'Order',
      size: 200,
      cell: ({ row }) => {
        const entry = row.original;
        const isSelected = entry.commission_entry_id === selectedEntryId;
        return (
          <button
            type="button"
            onClick={() => setSelectedEntryId(entry.commission_entry_id)}
            className="group min-w-0 text-left"
            aria-label={`Inspect ${commissionOrderReference(entry)}`}
          >
            <p className={`text-sm font-black transition-colors ${isSelected ? 'text-primary' : 'text-foreground group-hover:text-primary'}`}>
              Order {commissionOrderReference(entry)}
            </p>
            <DescriptionText className="text-[11px]">
              Entry {entry.commission_entry_id}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      cell: ({ row }) => (
        <RegistryStatus
          label={row.original.status}
          variant={commissionStatusVariant(row.original.status)}
        />
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      id: 'amount',
      accessorFn: entry => Number(entry.amount),
      header: 'Amount',
      size: 120,
      cell: ({ row }) => (
        <div className="min-w-[7rem]">
          <p className="text-sm font-black text-primary">${row.original.amount}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60">
            {row.original.currency}
          </p>
        </div>
      ),
    },
    {
      accessorKey: 'selling_tenant_id',
      header: 'Selling Tenant',
      size: 180,
      cell: ({ row }) => (
        <span className="font-mono text-[11px] font-bold text-foreground/80">
          {row.original.selling_tenant_id || 'Unknown'}
        </span>
      ),
    },
    {
      accessorKey: 'commission_plan_id',
      header: 'Plan',
      size: 180,
      cell: ({ row }) => (
        <span className="font-mono text-[11px] font-bold text-foreground/80">
          {row.original.commission_plan_id || 'Not snapshotted'}
        </span>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      size: 140,
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {formatDate(row.original.updated_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 80,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <button
            type="button"
            onClick={() => setSelectedEntryId(row.original.commission_entry_id)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            aria-label={`View details for ${commissionOrderReference(row.original)}`}
          >
            <Search className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ], [selectedEntryId]);
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: COMMISSION_LEDGER_SORT_FIELDS, registry: 'commission-ledger' });

  const metrics = workspace === 'reseller' && summary
    ? [
        { id: 'total-earned', label: 'Total Earned', value: `$${sumStatusAmounts(summary)}`, helperText: 'Lifetime earned commissions' },
        { id: 'total-paid', label: 'Total Paid', value: `$${sumStatusAmounts(summary, 'paid')}`, helperText: 'Commissions successfully paid out' },
        { id: 'pending-approval', label: 'Pending Approval', value: `${summary.commission_counts_by_status?.['earned'] || 0}`, helperText: 'Entries awaiting verification' },
        { id: 'orders', label: 'Order Count', value: `${summary.order_count}`, helperText: 'Total orders attributed to you' },
      ]
    : [
        { id: 'ledger-entries', label: 'Ledger Entries', value: `${entries.length}`, helperText: 'Commission records for the active catalog owner' },
        { id: 'ledger-total', label: 'Ledger Total', value: `$${sumLedgerAmount(entries)}`, helperText: entries[0]?.currency || 'Current ledger currency' },
        { id: 'paid-entries', label: 'Paid', value: `${entries.filter(entry => entry.paid || entry.status === 'paid').length}` },
        { id: 'open-entries', label: 'Open', value: `${entries.filter(entry => !(entry.paid || entry.status === 'paid')).length}` },
      ];

  const workspaceTitle = workspace === 'catalog-owner' ? 'Commerce Commission Ledger' : 'Reseller Commission Ledger';
  const workspaceDescription = workspace === 'catalog-owner'
    ? 'Inspect every durable commission entry for the active catalog-owner tenant across all selling sources.'
    : 'Inspect durable commission entries and payout lifecycle timestamps attributed to the active reseller tenant.';

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    activeSectionId: 'commission_ledger',
    searchQuery: query.search,
    searchPlaceholder: 'Search commission ledger...',
    onSearchChange: query.setSearch,
    error,
    blockingReason: 'Your current role does not have permission to inspect commission reporting.',
    onSelectSection: sectionId => navigation.navigate(buildLedgerSectionRoute(sectionId, workspace)),
    primaryAction: {
      id: 'refresh-ledger',
      label: 'Refresh',
      onAction: () => void loadLedger(),
      disabled: loading,
    },
    metrics,
  });

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      padded={false}
      titleContent={
        <CommerceBreadcrumbHeader
          breadcrumbs={[{ label: 'Commission Ledger' }]}
          title={workspaceTitle}
          description={workspaceDescription}
        />
      }
      takeoverActive={!!selectedEntryId}
      replaceShellHeaderWhenTakeoverActive={true}
      takeoverContent={
        <CommissionLedgerEntryView
          entry={selectedEntry}
          loading={detailLoading}
          onClose={() => setSelectedEntryId(null)}
        />
      }
    >
      <RegistryWorkspace>
        {summaryNotice ? (
          <CommerceRegistryUtilityBand className="shrink-0">
            <DescriptionText className="text-xs">{summaryNotice}</DescriptionText>
          </CommerceRegistryUtilityBand>
        ) : null}

        <RegistryTableSurface>
          <DataTable
            mode="manual"
            columns={columns}
            data={entries}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            columnFilters={columnFilters}
            onColumnFiltersChange={(updater) => {
              const next = typeof updater === 'function' ? updater(columnFilters) : updater;
              const statusFilter = next.find(entry => entry.id === 'status');
              query.setFacet('status', Array.isArray(statusFilter?.value) ? statusFilter.value.map(String) : []);
            }}
            globalFilter={query.search}
            rowSelection={rowSelection}
            pagination={query.pagination}
            onPaginationChange={query.onPaginationChange}
            manualPagination={true}
            totalRowCount={totalEntries}
            loading={loading}
            className="border-none rounded-none"
            paginationClassName="px-0 py-0"
            showPagination={true}
            emptyState={
              <div className="px-0 py-12 text-center">
                <p className="text-sm font-black text-foreground">No commission ledger entries found</p>
                <DescriptionText className="mt-1 text-xs">
                  {query.search || selectedStatuses.length > 0
                    ? 'No commission entries matched the current search or status filters.'
                    : 'Commission entries will appear here after priced orders accrue durable payout records.'}
                </DescriptionText>
              </div>
            }
            renderToolbar={(table) => (
              <DataTableToolbar
                table={table}
                searchValue={query.search}
                onSearchChange={query.setSearch}
                searchPlaceholder="Search all commission entries..."
                resultCount={totalEntries}
                className="border-b border-border/40 bg-transparent px-0 py-0"
                filters={
                  table.getColumn('status') && (
                    <DataTableFacetedFilter
                      title="Status"
                      selectedValues={new Set(table.getColumn('status')?.getFilterValue() as string[] ?? [])}
                      onFilterChange={(values) => table.getColumn('status')?.setFilterValue(Array.from(values))}
                      options={statusOptions.map(option => ({
                        label: option.label || option.value,
                        value: option.value,
                      }))}
                      counts={Object.fromEntries(statusOptions.map(option => [option.value, option.count]))}
                    />
                  )
                }
              />
            )}
          />
        </RegistryTableSurface>
      </RegistryWorkspace>
    </CommerceCatalogWorkspace>
  );
}
